import { ASSIGNED_BOOKING_TYPE, CREATE_BOOKING_TYPE, MODALS } from "../../config/params";
import { Box, Divider, ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import {
  CallHospitalIcon,
  CancelAppointmentIcon,
  ClinicLocationIcon,
  RescheduleIcon,
  UrgentVisitIcon,
} from "../Icons";
import { IAppointmentDetails, IGroupedAppointment } from "../../services/appointment-list/appointment-list.interface";
import React, { FC, useState } from "react";
import { fromUnixToFullDateNum, getDateEpochInTimezone } from "../../utils/unix-timestamp";
import { useBookingAppointmentTypeStore, useRescheduleAllow } from "../../hooks";

import GvAvatarCustom from "../GvAvatarCustom/GvAvatarCustom";
import { GvCustomMenu } from "../../styles/styledComponents";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { RESCHEDULE_SOURCE } from "../../navigation/params";
import { UrgentVisitStep } from "../../state/urgent-visit";
import { getContactString } from "../../shared/containers/ChangeHospitalContainer/ChangeHospitalContainer";
import { parseReasonsForVisit } from "../../shared/containers/AppointmentReviewContainer/AppointmentReviewContainer";
import { rescheduleSourceActions } from "../../state/recheduleSource";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useDispatch } from "react-redux";
import { useModal } from "../../context/ModalContext";
import { usePetStore } from "../../hooks/redux-hooks/pet-store/pet-store.service";
import { useUrgentVisitStore } from "../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { userActivityLogger } from "../../utils/mixpanel";

type IGroupedAppointmentCardProps = {
  group: IGroupedAppointment;
  isMenu?: boolean;
}

const GroupedAppointmentCard: FC<IGroupedAppointmentCardProps> = (props) => {
  const { group, isMenu = false } = props;
  const { appointments } = group;
  const firstVisit = appointments[0];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { petList } = usePetStore();
  const { openModal } = useModal();
  const dispatch = useDispatch();
  const { setSelectedVisit, setAppointmentDetails, setGroupedAppointments } = useAppointmentStore();
  const { setBookingAppointmentType, setAppointmentTypeIndicator } = useBookingAppointmentTypeStore();
  const {
    resetUrgentVisit, setRescheduleAppointmentId, setPetAppointmentMap,
    setStep: setUrgentStep, setSelectedPets: setUrgentSelectedPets,
    setPetReasons, setPetNotes, setSelectedDate,
  } = useUrgentVisitStore();

  const isUrgent = appointments.some(
    (appt) => appt.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
  );

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReschedule = () => {
    setGroupedAppointments(appointments);
    if (isUrgent) {
      setBookingAppointmentType(CREATE_BOOKING_TYPE.URGENT_BOOKING);
      setAppointmentTypeIndicator(ASSIGNED_BOOKING_TYPE.URGENT_BOOKING);
      resetUrgentVisit();
      setSelectedDate(getDateEpochInTimezone(
        appointments[0].start_at, appointments[0].location.timeZone,
      ));
      setRescheduleAppointmentId(appointments[0].id);
      const petApptMap: Record<string, string> = {};
      const groupType = appointments[0].appointmentCreateType;
      appointments
        .filter((appt) => appt.appointmentCreateType === groupType)
        .forEach((appt) => {
          petApptMap[appt.pet.id] = appt.id;
        });
      setPetAppointmentMap(petApptMap);
      setAppointmentDetails(appointments[0]);
      const petsToSelect = (petList || []).filter((p) =>
        Object.keys(petApptMap).includes(p.id));
      if (petsToSelect.length > 0) {
        setUrgentSelectedPets(petsToSelect);
        setUrgentStep(UrgentVisitStep.PET_SELECTION);
        appointments
          .filter((appt) => appt.appointmentCreateType === groupType)
          .forEach((appt) => {
            if (appt.reasonsForVisit) {
              const parsed = parseReasonsForVisit(appt.reasonsForVisit);
              setPetReasons(appt.pet.id, parsed.reasons);
              setPetNotes(appt.pet.id, parsed.notes);
            }
          });
      }
      openModal(MODALS.URGENT_VISIT_MODAL);
    } else {
      openModal(MODALS.GROUPED_APPOINTMENT_ACTION, undefined, { action: "reschedule" });
    }
  };

  const handleCancel = () => {
    setGroupedAppointments(appointments);
    openModal(MODALS.GROUPED_APPOINTMENT_ACTION, undefined, { action: "cancel" });
  };

  const formattedDateObject = fromUnixToFullDateNum(firstVisit?.start_at!,
    firstVisit?.location.timeZone);

  const handleVisitSelected = () => {
    setGroupedAppointments(appointments);
    setSelectedVisit(firstVisit);
    setAppointmentDetails(firstVisit);
    userActivityLogger(MIXPANEL_EVENTS.USER_CLICK_UPCOMING_VISIT,
      { owner_type: petList?.length === 1 ? "single_pet" : "multipet",
        no_of_pets: appointments.length,
      });

    openModal(MODALS.APPOINTMENT_REVIEW);
    dispatch(rescheduleSourceActions.setRescheduleSourceAction({ sourcePage: RESCHEDULE_SOURCE.DASHBOARD }));
  };

  const getCleanReason = (appt: IAppointmentDetails): string => {
    const raw = appt.reasonsForVisit || "";
    const match = raw.match(/,reasons:(.*)/);
    if (match?.[1]) return match[1];
    return raw || appt.appointmentType.split("-").join(" ");
  };

  const buildGroupTitle = (appts: IAppointmentDetails[]): string => {
    const normalize = (t: string) => t.trim().toLowerCase();
    const reasons = appts.map((a) => getCleanReason(a));
    const allSame = reasons.every((r) =>
      normalize(r) === normalize(reasons[0]));

    if (allSame) {
      const names = appts.map((a) => a.pet?.name).join(" + ");
      return `${names} ${reasons[0]}`;
    }

    return appts.map((appt, i) =>
      `${appt.pet?.name}'s ${reasons[i]}`,
    ).join(" + ");
  };

  const petDetails = appointments.map((appt) => ({
    name: appt.pet.name,
    image: appt.pet.image,
  }));

  const { rescheduleAllowed } = useRescheduleAllow(firstVisit?.start_at, firstVisit?.location.timeZone);

  const allowedToEdit = appointments.some((appt) => appt.allowedToEdit) ||
    (isUrgent && rescheduleAllowed);
  const avatarSize = 56;
  const avatarOffset = 24;

  return (
    <Box className="bg-white rounded-2xl p-2 cursor-pointer border border-transparent
    hover:border hover:border-dark-gv-yellow hover:bg-gv-yellow-light my-2 shadow-sm">
      <Box className="flex justify-between">
        <Box className="flex gap-4 items-center grow min-w-0" onClick={handleVisitSelected}>
          <Box
            className="relative"
            sx={{
              width: avatarSize,
              height: avatarSize +
                (petDetails.length - 1) * avatarOffset,
              flexShrink: 0,
            }}
          >
            {petDetails.map((pet, index) => (
              <Box
                key={index}
                className="absolute"
                sx={{
                  top: index * avatarOffset,
                  left: 0,
                  zIndex: index + 1,
                }}
              >
                <GvAvatarCustom
                  image={pet.image}
                  name={pet.name}
                  width={avatarSize}
                  height={avatarSize}
                  className="border-2 border-white rounded-full"
                />
              </Box>
            ))}
            {isUrgent && (
              <Box
                className="absolute bg-taupe-4 rounded-full
                  w-6 h-6 flex items-center justify-center"
                sx={{ bottom: -4, right: -4, zIndex: 10 }}
              >
                <UrgentVisitIcon width={16} height={16} />
              </Box>
            )}
          </Box>
          <Box className="flex flex-col gap-1 min-w-0">
            <Box className="flex gap-1 items-center border-b-2 border-dark-gv-yellow py-1 w-fit">
              <Typography variant="body1" fontWeight={600}>{formattedDateObject.weekday}</Typography>
              <hr className="text-gray-3 h-3 border-x" />
              <Typography variant="body1" className="flex gap-1 items-center">
                <span>{formattedDateObject.month}</span>
                <span>{formattedDateObject.day}</span>
                <span>&#x2022;</span>
                <span>
                  {formattedDateObject.time}
                </span>
              </Typography>
            </Box>
            <Typography
              variant="h3"
              className="font-semibold capitalize"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {buildGroupTitle(appointments)}
            </Typography>
            <Box className="flex items-center gap-1">
              <ClinicLocationIcon width={14} height={14} color="#908E8D" />
              <Typography variant="body1" sx={{ color: (theme) => theme.palette.gray.dark }}>
                {firstVisit.location?.locationName}</Typography>
            </Box>
          </Box>
        </Box>
        <div id="grouped-appointment-button" aria-haspopup="true"
          aria-controls={open ? "grouped-appointment-menu" : undefined}
          aria-expanded={open ? "true" : undefined} onClick={handleClick}>
          <MoreHorizIcon sx={{ cursor: "pointer" }} />
        </div>
        {isMenu &&
          <GvCustomMenu autoFocus={false} transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "top" }}
            id="grouped-appointment-menu" anchorEl={anchorEl} open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "grouped-appointment-button",
            }}
            classes={{ list: "!p-0" }}
          >
            {allowedToEdit &&
            <MenuItem
              onClick={() => {
                handleReschedule();
                handleClose();
              }}
            >
              <ListItemIcon>
                <RescheduleIcon />
              </ListItemIcon>
              <ListItemText>Reschedule</ListItemText>
            </MenuItem>}
            <Divider className="w-full !m-0" />
            {allowedToEdit &&
            <MenuItem
              onClick={() => {
                handleCancel();
                handleClose();
              }}
            >
              <ListItemIcon>
                <CancelAppointmentIcon />
              </ListItemIcon>
              <ListItemText>Cancel</ListItemText>
            </MenuItem>}
            <Divider className="w-full !m-0" />
            <MenuItem
              onClick={() => {
                handleClose();
              }}
            >
              <ListItemIcon>
                <CallHospitalIcon />
              </ListItemIcon>
              <ListItemText>
                <a href={getContactString(firstVisit.location.phoneNumber)}>Call Hospital</a>
              </ListItemText>
            </MenuItem>
          </GvCustomMenu>}
      </Box>
    </Box>
  );
};

export default GroupedAppointmentCard;
